import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Compass, Play, X } from 'lucide-react';
import { useWalkthrough } from '@/app/providers';
import { STEPS } from '@/shared/walkthrough-steps';

// Walkthrough (v2) — bottom-right compact chip.
//
// Rest state: a small chip showing the step indicator ("1/14") and a
// next-step chevron. Hovering or focusing the chip expands a popover
// upward with the full caption, prev/next, free-explore toggle, and
// dismiss. This replaces the v1 full-width bottom pill which dominated
// the viewport and obscured the caption-vs-controls hierarchy.
//
// Behaviour preserved from v1:
//   - prev/next advance the step counter and navigate to STEPS[next].hash
//   - dismiss → state.dismissed = true, swaps the chip for a resume chip
//     in the same bottom-right slot
//   - free explore → caption switches to "Free explore — paused at step N"
//     and prev/next become hidden (the user is off-spine)
//
// Gate (2026-05-17 audit D1): the walkthrough is sales-demo-only. Hidden
// by default; rendered only when `?demo=true` is present in the URL.
// No localStorage writes when the gate is closed (no localStorage in the
// provider at all today — kept that way intentionally).
//
// Hook (2026-05-17 audit D2): use `useIsDemoMode()` to gate any
// walkthrough-adjacent effect (e.g. `useWalkthroughSync`) so persona
// auto-flip + indicator sync don't run for default users.
export function useIsDemoMode(): boolean {
  const [params] = useSearchParams();
  return params.get('demo') === 'true';
}

export function Walkthrough() {
  const isDemo = useIsDemoMode();
  const {
    currentStep,
    total,
    freeExplore,
    dismissed,
    next,
    prev,
    toggleFreeExplore,
    dismiss,
    resume,
  } = useWalkthrough();

  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Navigate only when the step counter itself advances (prev/next). URL
  // changes alone must NOT pull the user back — clicking off-spine should
  // auto-flip Free Explore (handled in useWalkthroughSync), not yank.
  //
  // 2026-05-17 audit D2 fix: location.pathname / location.search are read
  // inside the effect via a ref but NOT in the dep array. Watching them
  // re-fired the effect on every navigation, which is the root of the
  // observed bounce-to-/dashboard pattern (deep-links, drawer CTAs, outline
  // clicks all triggered this effect). Now the effect only runs when
  // currentStep itself changes via prev/next.
  const locationRef = useRef(location);
  locationRef.current = location;
  const prevStepRef = useRef(currentStep);
  useEffect(() => {
    if (dismissed || freeExplore) {
      prevStepRef.current = currentStep;
      return;
    }
    if (currentStep === prevStepRef.current) return;
    prevStepRef.current = currentStep;
    const target = STEPS[currentStep]?.hash;
    if (!target) return;
    const loc = locationRef.current;
    const here = loc.pathname + (loc.search ?? '');
    if (here !== target) navigate(target);
  }, [currentStep, freeExplore, dismissed, navigate]);

  // Close the popover when clicking outside or pressing Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // 2026-05-17 audit D1 gate: walkthrough is sales-demo-only.
  // Hidden by default; only renders when `?demo=true` is in the URL.
  // No persona-gate today (no sales/demo persona exists in the union); the
  // URL param is the single source of truth. If a sales persona is added
  // later, OR it into this guard.
  if (!isDemo) return null;

  if (dismissed) {
    return (
      <button
        type="button"
        data-comment-anchor="Walkthrough"
        data-review-mode="true"
        className="walk-chip-resume glass-strip"
        onClick={resume}
        aria-label="Resume walkthrough"
      >
        <Play size={12} aria-hidden="true" />
        <span>Resume</span>
      </button>
    );
  }

  const step = STEPS[currentStep];
  const stepN = currentStep + 1;
  const caption = freeExplore
    ? `Free explore — paused at step ${stepN}.`
    : step?.caption ?? '';

  const tagClass = ['walk-chip-tag', freeExplore ? 'paused' : ''].filter(Boolean).join(' ');

  return (
    <div
      ref={wrapRef}
      data-comment-anchor="Walkthrough"
      data-review-mode="true"
      className={['walk-chip-wrap', open ? 'open' : ''].filter(Boolean).join(' ')}
      role="status"
      aria-live="polite"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="walk-chip glass-strip"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Walkthrough — step ${stepN} of ${total}`}
      >
        <span className={tagClass}>
          {stepN}/{total}
        </span>
        {!freeExplore && (
          <ChevronRight size={14} aria-hidden="true" className="walk-chip-hint" />
        )}
        {freeExplore && (
          <Compass size={12} aria-hidden="true" className="walk-chip-hint" />
        )}
      </button>

      <div className="walk-chip-popover glass-strip" role="dialog" aria-label="Walkthrough controls">
        <div className="walk-chip-caption">{caption}</div>
        <div className="walk-chip-row">
          <button
            type="button"
            className="walk-chip-nav"
            onClick={prev}
            disabled={currentStep === 0 || freeExplore}
            aria-label="Previous step"
          >
            <ChevronLeft size={14} aria-hidden="true" />
          </button>
          <span className="walk-chip-counter">
            {stepN} / {total}
          </span>
          <button
            type="button"
            className="walk-chip-nav"
            onClick={next}
            disabled={currentStep === total - 1 || freeExplore}
            aria-label="Next step"
          >
            <ChevronRight size={14} aria-hidden="true" />
          </button>
          <span className="walk-chip-divider" />
          <button
            type="button"
            className={['walk-chip-free', freeExplore ? 'on' : ''].filter(Boolean).join(' ')}
            onClick={toggleFreeExplore}
            aria-pressed={freeExplore}
          >
            <Compass size={12} aria-hidden="true" />
            <span>Free explore</span>
          </button>
          <button
            type="button"
            className="walk-chip-dismiss"
            onClick={dismiss}
            aria-label="Dismiss walkthrough"
          >
            <X size={12} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
